import { useState } from 'react';
import type { ReactNode } from 'react';
import { CONCIERGE_SCOPE_LIST, SCOPES } from '@yourstore/domain';
import { useAction, useApi } from '../../context';
import { Confirm, Failure, OnceBox } from '../../bits';
import { Chip, Panel } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type { ApiKeyRow, KeyEnv, KeyIssued } from '../../../wire';
import { Dialog } from './Dialog';
import { day, featureGroup, featureName, relative, scopeSentence } from './words';

/**
 * **Partner access keys** — the credentials, as a plain list, and the one
 * dialog in this console that handles a secret.
 *
 * The three rules of the card this replaces still hold, and are the CLI's:
 *
 *  1. **The plaintext is shown once**, in the dialog that made it, and
 *     dismissing it drops it. The dialog cannot be closed any other way while
 *     it is on screen — not by Esc, not by a Cancel — because closing the form
 *     must not be able to take the key with it unread.
 *  2. **Two working keys per partner.** A database constraint, not a
 *     convention. The dialog says so in words when the partner typed already
 *     holds two, and the server's own refusal still prints if it fires.
 *  3. **Nothing is granted for a feature the business has not turned on.** Those
 *     boxes are disabled with "Turn on … first", because the effective grant is
 *     what the key says *and* what the business has; a key whose written
 *     permissions differ from its real ones is a support ticket waiting to
 *     happen. The server still refuses independently.
 *
 * What changed is the vocabulary: a scope is shown as what it lets a partner
 * do, grouped by the feature it belongs to, and the table of prefixes, slots
 * and scope tokens is a list a person reads — "aiconcierge · made Sep 30 ·
 * used 2 minutes ago · test · Revoke". The prefix is gone from the row: it is
 * only the environment marker (`ys_test_`), which the test/live label already
 * says, so it told two keys apart no better than nothing. A key is named by
 * who holds it and when it was made.
 *
 * The concierge's own key is **not** listed here while the concierge card is
 * showing it (plan: "not repeated in three other places"); revoked keys are
 * history and are all behind "Show revoked", whoever held them.
 */
export function KeysCard({
  tenantId,
  keys,
  enabledModules,
  conciergeOwns,
  onChanged,
}: {
  tenantId: string;
  keys: readonly ApiKeyRow[];
  enabledModules: readonly string[];
  /** The concierge card is showing the concierge's own key — leave it out here. */
  conciergeOwns: boolean;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const [making, setMaking] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const revoke = useKeyRevoke(tenantId, onChanged);

  const isConcierge = (k: ApiKeyRow): boolean => conciergeOwns && k.integration === 'aiconcierge';
  const working = keys.filter((k) => k.status === 'active' && !isConcierge(k));
  const hidden = keys.filter((k) => k.status === 'active' && isConcierge(k)).length;
  const revoked = keys.filter((k) => k.status === 'revoked');

  return (
    <Panel
      title={t('connections.keys.title')}
      className="conn-card"
      actions={
        working.length > 0 ? (
          <Chip tone="ok">{t('connections.keys.chip', { count: working.length })}</Chip>
        ) : undefined
      }
    >
      {working.length === 0 ? (
        <p className="muted">
          {hidden > 0 ? t('connections.keys.noneButConcierge') : t('connections.keys.none')}
        </p>
      ) : (
        <ul className="conn-list">
          {working.map((k) => (
            <KeyRow key={k.key_id} row={k} onRevoke={revoke.ask} />
          ))}
        </ul>
      )}
      {working.length > 0 && hidden > 0 ? (
        <p className="muted conn-small">{t('connections.keys.conciergeElsewhere')}</p>
      ) : null}

      {revoke.failure}

      <div className="row-actions">
        <button
          type="button"
          className="button button--sm"
          onClick={() => {
            setMaking(true);
          }}
        >
          {t('connections.keys.make')}
        </button>
        {revoked.length > 0 ? (
          <button type="button" className="linkish" onClick={() => setShowRevoked((v) => !v)}>
            {showRevoked
              ? t('connections.keys.hideRevoked')
              : t('connections.keys.showRevoked', { count: revoked.length })}
          </button>
        ) : null}
      </div>

      {showRevoked && revoked.length > 0 ? (
        <ul className="conn-list conn-list--muted">
          {revoked.map((k) => (
            <KeyRow key={k.key_id} row={k} onRevoke={revoke.ask} />
          ))}
        </ul>
      ) : null}

      {making ? (
        <MakeKeyDialog
          tenantId={tenantId}
          keys={keys}
          enabledModules={enabledModules}
          onMade={onChanged}
          onClose={() => setMaking(false)}
        />
      ) : null}
      {revoke.dialog}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* One key                                                             */
/* ------------------------------------------------------------------ */

/**
 * "aiconcierge · made Sep 30 · used 2 minutes ago · test" and a Revoke. Shared
 * with the concierge card, which lists the concierge's own key the same way.
 */
export function KeyRow({
  row,
  onRevoke,
}: {
  row: ApiKeyRow;
  onRevoke: (row: ApiKeyRow) => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const active = row.status === 'active';

  return (
    <li className="conn-row">
      <div className="conn-row__main">
        <div className="conn-row__line">
          <strong>{row.integration}</strong>
          <span className="muted">
            {' · '}
            <span title={row.created_at}>
              {t('connections.keys.made', { when: day(row.created_at, locale) })}
            </span>
            {' · '}
            {row.last_used_at === null ? (
              t('connections.keys.neverUsed')
            ) : (
              <span title={row.last_used_at}>
                {t('connections.keys.used', { when: relative(row.last_used_at, locale) })}
              </span>
            )}
            {active || row.revoked_at === null ? null : (
              <>
                {' · '}
                <span title={row.revoked_at}>
                  {t('connections.keys.revokedOn', { when: day(row.revoked_at, locale) })}
                </span>
              </>
            )}
          </span>{' '}
          <Chip tone={row.env === 'live' ? 'accent' : 'plain'}>{envWord(t, row.env)}</Chip>
        </div>
        {row.scopes.length === 0 ? null : (
          <details className="conn-more">
            <summary>{t('connections.keys.mayDo')}</summary>
            <ul className="conn-bullets">
              {row.scopes.map((s) => (
                <li key={s}>{scopeSentence(t, s)}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {active ? (
        <button
          type="button"
          className="button button--sm button--danger"
          onClick={() => onRevoke(row)}
        >
          {t('connections.keys.revoke')}
        </button>
      ) : null}
    </li>
  );
}

function envWord(t: T, env: string): string {
  return env === 'live' ? t('connections.keys.env.live') : t('connections.keys.env.test');
}

/* ------------------------------------------------------------------ */
/* Revoke, confirmed                                                   */
/* ------------------------------------------------------------------ */

/**
 * The revoke confirm, as a hook so the concierge card and this one ask the
 * same question in the same words. It keeps the honest number: the key
 * directory is cached in memory for 15 seconds, and rounding that to "revoked"
 * is how an operator concludes it did not work and does it twice.
 */
export function useKeyRevoke(
  tenantId: string,
  onChanged: () => void,
): { ask: (row: ApiKeyRow) => void; dialog: ReactNode; failure: ReactNode } {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const action = useAction();
  const [row, setRow] = useState<ApiKeyRow | null>(null);

  const confirm = async (): Promise<void> => {
    if (row === null) return;
    const ok = await action.run(async () => {
      await api.revokeKey(tenantId, row.key_id);
    });
    if (ok) {
      setRow(null);
      onChanged();
    }
  };

  return {
    ask: (next) => {
      action.clear();
      setRow(next);
    },
    failure: row === null && action.failure !== null ? <Failure failure={action.failure} /> : null,
    dialog:
      row === null ? null : (
        <Confirm
          title={t('connections.keys.revokeTitle', {
            integration: row.integration,
            made: day(row.created_at, locale),
          })}
          consequence={t('connections.keys.revokeConsequence')}
          confirmLabel={t('connections.keys.revokeLabel')}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void confirm()}
          onCancel={() => {
            action.clear();
            setRow(null);
          }}
        />
      ),
  };
}

/* ------------------------------------------------------------------ */
/* Make a key                                                          */
/* ------------------------------------------------------------------ */

/** `SCOPES` grouped by the feature each belongs to, in the table's own order. */
function scopeGroups(): { module: string; scopes: string[] }[] {
  const groups: { module: string; scopes: string[] }[] = [];
  for (const spec of SCOPES) {
    const group = groups.find((g) => g.module === spec.module);
    if (group === undefined) groups.push({ module: spec.module, scopes: [spec.scope] });
    else group.scopes.push(spec.scope);
  }
  return groups;
}

function MakeKeyDialog({
  tenantId,
  keys,
  enabledModules,
  onMade,
  onClose,
}: {
  tenantId: string;
  keys: readonly ApiKeyRow[];
  enabledModules: readonly string[];
  onMade: () => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const issue = useAction();
  const enabled = new Set(enabledModules);
  const grantable = (scope: string): boolean => {
    const spec = SCOPES.find((s) => s.scope === scope);
    return spec !== undefined && enabled.has(spec.module);
  };

  const [integration, setIntegration] = useState('aiconcierge');
  const [env, setEnv] = useState<KeyEnv>('test');
  // The concierge's own set, less anything the business cannot grant: a box
  // that starts ticked and disabled would be a refusal waiting to be clicked.
  const [scopes, setScopes] = useState<string[]>(CONCIERGE_SCOPE_LIST.filter(grantable));
  const [issued, setIssued] = useState<KeyIssued | null>(null);

  const who = integration.trim();
  const holding = keys.filter((k) => k.status === 'active' && k.integration === who).length;

  const submit = async (): Promise<void> => {
    const ok = await issue.run(async () => {
      setIssued(await api.issueKey(tenantId, { integration: who, env, scopes }));
    });
    if (ok) onMade();
  };

  /*
   * The plaintext, and nothing else: the form is gone, and the dialog can only
   * be closed by saying it has been stored. Dismissing drops it — there is no
   * "show again", because a console that could show it again would be a
   * console that kept it.
   */
  if (issued !== null) {
    return (
      <Dialog title={t('connections.keys.dialog.title')} onClose={null}>
        <OnceBox
          label={t('connections.keys.onceLabel', {
            integration: issued.integration,
            env: envWord(t, issued.env),
          })}
          value={issued.plaintext}
          note={issued.plaintext_note}
          onDismiss={() => {
            setIssued(null);
            onClose();
          }}
        />
        {issued.warnings.map((w) => (
          <p key={w} className="warn">
            {w}
          </p>
        ))}
      </Dialog>
    );
  }

  return (
    <Dialog title={t('connections.keys.dialog.title')} onClose={onClose} busy={issue.busy} wide>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field__label">{t('connections.keys.dialog.who')}</span>
          <input
            aria-label={t('connections.keys.dialog.who')}
            value={integration}
            onChange={(e) => setIntegration(e.target.value)}
            placeholder="aiconcierge"
          />
          <span className="field__hint">{t('connections.keys.dialog.whoHint')}</span>
        </label>
        {holding >= 2 ? (
          <p className="warn" role="note">
            {t('connections.keys.dialog.twoActive', { integration: who })}
          </p>
        ) : null}

        <fieldset className="conn-choices">
          <legend>{t('connections.keys.dialog.env')}</legend>
          {(['test', 'live'] as const).map((value) => (
            <label key={value} className="conn-choice">
              <input
                type="radio"
                name="key-env"
                checked={env === value}
                onChange={() => setEnv(value)}
              />
              <span>
                <strong>
                  {value === 'test'
                    ? t('connections.keys.dialog.env.test')
                    : t('connections.keys.dialog.env.live')}
                </strong>
                <span className="conn-choice__what">
                  {value === 'test'
                    ? t('connections.keys.dialog.env.test.what')
                    : t('connections.keys.dialog.env.live.what')}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="conn-scopes">
          <p className="field__label">{t('connections.keys.dialog.may')}</p>
          <p className="field__hint">{t('connections.keys.dialog.mayHint')}</p>
          {scopeGroups().map((group) => {
            const on = enabled.has(group.module);
            return (
              <fieldset
                key={group.module}
                className={on ? 'conn-scope-group' : 'conn-scope-group is-blocked'}
              >
                <legend>
                  {featureGroup(t, group.module)}
                  {on ? null : (
                    <span className="conn-blocked">
                      {' · '}
                      {t('connections.keys.dialog.turnOn', {
                        feature: featureName(t, group.module),
                      })}
                    </span>
                  )}
                </legend>
                {group.scopes.map((scope) => (
                  <label key={scope} className="scope">
                    <input
                      type="checkbox"
                      disabled={!on}
                      checked={on && scopes.includes(scope)}
                      onChange={(e) =>
                        setScopes((list) =>
                          e.target.checked ? [...list, scope] : list.filter((x) => x !== scope),
                        )
                      }
                    />
                    <span>{scopeSentence(t, scope)}</span>
                  </label>
                ))}
              </fieldset>
            );
          })}
        </div>

        {scopes.length === 0 ? (
          <p className="muted">{t('connections.keys.dialog.nothingTicked')}</p>
        ) : null}
        {issue.failure !== null ? <Failure failure={issue.failure} /> : null}

        <div className="modal__actions">
          <button type="button" className="button" onClick={onClose} disabled={issue.busy}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={issue.busy || scopes.length === 0 || who === ''}
          >
            {issue.busy ? t('connections.keys.dialog.making') : t('connections.keys.dialog.make')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
